/**
 * CoordinateSystemModal — Edit a design model's coordinate system metadata.
 *
 * Persists to `designModel.coordinateSystem` via PUT /scan3d/models/{id}.
 * The shape mirrors what `alignment_engine.apply_coordinate_system` already
 * understands, so the math layer can consume edits immediately.
 */
import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { X, Compass, Save } from 'lucide-react';

const TYPES = [
  { value: 'Local', label: 'Local (model origin)' },
  { value: 'Project', label: 'Project (shared origin across disciplines)' },
  { value: 'UTM', label: 'UTM (Universal Transverse Mercator)' },
  { value: 'Custom', label: 'Custom (transform via origin + north angle)' },
];
const UNITS = ['millimeters', 'meters', 'feet', 'inches'];

export default function CoordinateSystemModal({ projectId, model, onClose, onSaved }) {
  const initial = model?.coordinateSystem || {};
  const [type, setType] = useState(initial.type || 'Local');
  const [originX, setOriginX] = useState(initial.origin?.x ?? 0);
  const [originY, setOriginY] = useState(initial.origin?.y ?? 0);
  const [originZ, setOriginZ] = useState(initial.origin?.z ?? 0);
  const [northAngle, setNorthAngle] = useState(initial.northAngle ?? 0);
  const [units, setUnits] = useState(initial.units || 'millimeters');
  const [datum, setDatum] = useState(initial.datum || '');
  const [utmZone, setUtmZone] = useState(initial.utmZone || '');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const close = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  const save = async () => {
    setBusy(true);
    try {
      const coordinateSystem = {
        type,
        origin: { x: +originX, y: +originY, z: +originZ },
        northAngle: +northAngle,
        units,
        ...(datum ? { datum } : {}),
        ...(type === 'UTM' && utmZone ? { utmZone } : {}),
      };
      await api.put(`/v1/projects/${projectId}/scan3d/models/${model.id}`, { coordinateSystem });
      toast.success('Coordinate system updated');
      onSaved?.(coordinateSystem);
      onClose();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3" style={{ background: 'rgba(0,0,0,0.7)' }}
         data-testid="coord-system-modal" role="dialog" aria-modal="true">
      <div className="rounded-xl max-w-lg w-full p-4 space-y-3" style={{ background: '#0f1722', border: '1px solid #1f2937', color: '#fff' }}>
        <div className="flex items-center gap-2">
          <Compass className="w-4 h-4 text-cyan-400" />
          <div className="font-semibold">Coordinate System — {model?.modelCode || model?.modelName}</div>
          <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-zinc-800" aria-label="Close" data-testid="coord-system-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-[10px] opacity-60">
          Editing the coordinate system also affects how alignment transforms project this model into the scan world.
        </p>

        <label className="block text-xs">
          <span className="opacity-70">Type</span>
          <select value={type} onChange={e => setType(e.target.value)}
                  className="block mt-1 w-full px-2 py-1 rounded"
                  style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
                  data-testid="coord-system-type">
            {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </label>

        <div>
          <span className="text-xs opacity-70">Origin (in {units})</span>
          <div className="grid grid-cols-3 gap-2 mt-1">
            {[['X', originX, setOriginX, 'coord-system-origin-x'],
              ['Y', originY, setOriginY, 'coord-system-origin-y'],
              ['Z', originZ, setOriginZ, 'coord-system-origin-z']].map(([lbl, val, setter, tid]) => (
              <label key={lbl} className="text-[10px] opacity-70">
                {lbl}
                <input type="number" value={val} onChange={e => setter(e.target.value)} step="0.001"
                       className="block w-full mt-0.5 px-2 py-1 rounded font-mono text-xs"
                       style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
                       data-testid={tid} />
              </label>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs">
            <span className="opacity-70">North angle (deg, CCW from +Y)</span>
            <input type="number" value={northAngle} onChange={e => setNorthAngle(e.target.value)} step="0.1"
                   className="block w-full mt-1 px-2 py-1 rounded font-mono"
                   style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
                   data-testid="coord-system-north-angle" />
          </label>
          <label className="text-xs">
            <span className="opacity-70">Units</span>
            <select value={units} onChange={e => setUnits(e.target.value)}
                    className="block w-full mt-1 px-2 py-1 rounded"
                    style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
                    data-testid="coord-system-units">
              {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            </select>
          </label>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs">
            <span className="opacity-70">Datum (e.g. WGS84, ED50)</span>
            <input value={datum} onChange={e => setDatum(e.target.value)} placeholder="WGS84"
                   className="block w-full mt-1 px-2 py-1 rounded"
                   style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
                   data-testid="coord-system-datum" />
          </label>
          {type === 'UTM' && (
            <label className="text-xs">
              <span className="opacity-70">UTM Zone (e.g. 39N)</span>
              <input value={utmZone} onChange={e => setUtmZone(e.target.value)} placeholder="39N"
                     className="block w-full mt-1 px-2 py-1 rounded"
                     style={{ background: '#0a0e14', border: '1px solid #1f2937', color: '#fff' }}
                     data-testid="coord-system-utm-zone" />
            </label>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <button onClick={onClose}
                  className="px-3 py-1.5 rounded text-xs"
                  style={{ background: '#0a0e14', color: '#9ca3af', border: '1px solid #1f2937' }}
                  data-testid="coord-system-cancel">
            Cancel
          </button>
          <button onClick={save} disabled={busy}
                  className="flex items-center gap-1 px-3 py-1.5 rounded text-xs font-semibold"
                  style={{ background: '#06b6d4', color: '#000', opacity: busy ? 0.7 : 1 }}
                  data-testid="coord-system-save">
            <Save className="w-3 h-3" /> {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
